import { Constants, TextUtilities } from '@pwc/pwc-deals-web-common';
import React, { useEffect, useMemo } from 'react';
import { debounce } from 'lodash';
import { useParams } from 'react-router-dom';
import cloneDeep from 'lodash/cloneDeep';
import { revertEscapedComments } from '../../../helpers/excelHiddenColumnsRemover';
import RichTextForm from '../../RichTextForm/RichTextForm';
import {
  getExcelLibraryItemsByGroup,
  getPptLibraryItemsByGroup,
} from '../../../../features/contentLibrary/libraryItemService';
import './RichTextLibraryItemCard.scss';
import { mapToJunctionStyles } from './utils';

const getLinkedObjects = (value) => {
  const content = new DOMParser().parseFromString(value, 'text/html');
  const placeholders = content.getElementsByClassName('placeholder');
  // eslint-disable-next-line no-underscore-dangle
  const _linkedObjects = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const placeholder of placeholders) {
    const { id } = placeholder;
    // eslint-disable-next-line no-underscore-dangle
    const placeholderContent = window.__LIBRARYITEMSTORE__.get(id);
    if (placeholderContent) {
      const libraryItemL = cloneDeep(placeholderContent);
      delete libraryItemL.rendered;
      _linkedObjects.push(libraryItemL);
    }
  }

  return _linkedObjects;
};

const RichTextLibraryItemCard = ({
  libraryItemId,
  panel,
  panelNumber,
  onRichTextContentUpdate,
  setIsDragDisabled,
  innerRef,
  isDragging,
  setEditor,
  panels,
  ...props
}) => {
  const initialData = useMemo(() => {
    return panel.content || '';
  }, [panel]);

  const { projectId } = useParams();

  const process = async () => {
    const content = new DOMParser().parseFromString(
      TextUtilities.parseRichText(panel.isShared, initialData).replace(
        Constants.EMPTY_RICH_TEXT_PLACEHOLDER,
        ''
      ),
      'text/html'
    );
    const placeholders = content.getElementsByClassName('placeholder');
    // eslint-disable-next-line no-restricted-syntax
    for (const placeholder of placeholders) {
      const { id } = placeholder;
      const linkedObjects = getLinkedObjects();
      const libraryItemL = linkedObjects.find((o) => o.id.toString() === id);
      const { contentType } = libraryItemL;

      if (libraryItemL) {
        if (contentType === 302 || contentType === 'ExcelNamedRange') {
          // eslint-disable-next-line no-await-in-loop
          const rendered = await getExcelLibraryItemsByGroup(
            projectId,
            libraryItemL.request
          );
          libraryItemL.rendered = rendered.fileContents[0].value;
        } else if (contentType === 300 || contentType === 'ExcelChart') {
          // eslint-disable-next-line no-await-in-loop
          const rendered = await getExcelLibraryItemsByGroup(
            projectId,
            libraryItemL.request
          );
          libraryItemL.rendered = rendered.fileContents[0].value;
        } else if (contentType === 500 || contentType === 'PowerpointGroup') {
          // eslint-disable-next-line no-await-in-loop
          const rendered = await getPptLibraryItemsByGroup(
            projectId,
            libraryItemL.request
          );
          libraryItemL.rendered = rendered.fileContents[0].value;
        }

        libraryItemL.rendered = mapToJunctionStyles(libraryItemL.rendered);
        // eslint-disable-next-line no-console,no-underscore-dangle
        window.__LIBRARYITEMSTORE__.add(libraryItemL);
      }
    }
    if (placeholders.length) {
      const linkedObjects = getLinkedObjects(content.body.innerHTML);
      onRichTextContentUpdate(
        panelNumber,
        content.body.innerHTML,
        linkedObjects
      );
    }
  };
  useEffect(() => {
    setIsDragDisabled(false);
    // TODO: why we're running this function
    process().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  const handleOnBlur = () => {
    setIsDragDisabled(false);
  };

  const handleOnChange = debounce((value) => {
    const linkedObjects = getLinkedObjects(value);
    onRichTextContentUpdate(panelNumber, value, linkedObjects);
  }, 300);

  const className = `rich-text-library-item-edit css-override ${
    isDragging ? 'dragging' : ''
  }`;

  /*
   * predefinedRichText is predefined value which is set at the beginning,
   * RTEditor can change itself - no need to set it explicitly on each update
   */
  const predefinedRichText = useMemo(
    () => revertEscapedComments(panel.content || panel.sharedContent),
    // eslint-disable-next-line
    []
  );

  const RichTextEditor = useMemo(() => {
    return (
      <RichTextForm
        richText={predefinedRichText}
        setCurrentRichText={handleOnChange}
        onBlur={handleOnBlur}
        onFocus={() => {
          setIsDragDisabled(true);
        }}
        setEditor={setEditor}
        libraryItemId={libraryItemId}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [predefinedRichText, panels]);

  return (
    <div
      ref={innerRef}
      {...props}
      className={className}
      style={{ zIndex: 6 - panelNumber }}
    >
      {RichTextEditor}
    </div>
  );
};

export default RichTextLibraryItemCard;
